import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { getZodDefaults, type WikiSpaceSchema, wikiSpaceSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { WikiSpaceForm } from "@/forms/wiki-space-form";
import { client } from "@/hono";

/** Props Interface */
interface AddWikiSpaceModalProps extends ModalProps {
	parentSpaceId?: string;
	onComplete?: (spaceId: string) => void;
}

export function AddWikiSpaceModal({
	isOpen,
	onClose,
	parentSpaceId,
	onComplete,
}: AddWikiSpaceModalProps) {
	/** Hooks */
	const qc = useQueryClient();
	const { t } = useTranslation();
	const methods = useForm({
		defaultValues: async () => {
			const val = getZodDefaults(wikiSpaceSchema);
			if (parentSpaceId) val.parentSpaceId = parentSpaceId;
			return val;
		},
		resolver: resolver(wikiSpaceSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: WikiSpaceSchema) {
		const response = await client.wiki.space.$post({
			json,
		});
		await qc.invalidateQueries({
			predicate: invalidate(["wiki-spaces"]),
		});
		const data = await responseHandler(response);
		onClose();
		if (data.wikiSpace) {
			onComplete?.(data.wikiSpace.id);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("wiki.add_space")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<WikiSpaceForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
